import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  ClipboardCheck,
  UserCheck,
  Clock,
  Calendar,
  Copy,
  Check,
  Palette,
  ExternalLink,
  Loader2,
  MessageCircle,
  Share2,
  QrCode
} from 'lucide-react';
import CoupleNavigation from './CoupleNavigation';
import { coupleAuth, wedding } from '../../services/api';

function CoupleDashboard() {
  const navigate = useNavigate();
  const [couple, setCouple] = useState(null);
  const [page, setPage] = useState(null);
  const [rsvpStats, setRsvpStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedInvite, setCopiedInvite] = useState(false);
  const [isPublished, setIsPublished] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [meRes, pageRes, rsvpRes] = await Promise.all([
        coupleAuth.getMe(),
        wedding.getMyPage(),
        wedding.getRsvpDashboard()
      ]);

      setCouple(meRes.data);
      setPage(pageRes.data);
      setIsPublished(pageRes.data?.is_published || false);
      setRsvpStats(rsvpRes.data);
    } catch (err) {
      console.error('Dashboard load error:', err);
      if (err.response?.status === 401) {
        coupleAuth.logout();
        navigate('/couple/login');
        return;
      }
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePublishToggle = async () => {
    try {
      const newState = !isPublished;
      await wedding.publishPage(newState);
      setIsPublished(newState);
    } catch (err) {
      console.error('Publish toggle error:', err);
    }
  };

  const handleCopyUrl = () => {
    const url = `https://bridalconnect.co.za/${couple?.slug || page?.slug}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getDaysUntilWedding = () => {
    const weddingDate = couple?.wedding_date || page?.wedding_date;
    if (!weddingDate) return null;
    const diff = new Date(weddingDate) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const coupleNames = couple
    ? [couple.partner1_name, couple.partner2_name].filter(Boolean).join(' & ')
    : '';

  const slug = couple?.slug || page?.slug;
  const daysLeft = getDaysUntilWedding();

  const stats = [
    {
      label: 'Total Guests',
      value: rsvpStats?.total_guests ?? 0,
      icon: Users,
      color: 'bg-blue-50 text-blue-600'
    },
    {
      label: 'RSVPs Received',
      value: rsvpStats?.total_rsvps ?? 0,
      icon: ClipboardCheck,
      color: 'bg-purple-50 text-purple-600'
    },
    {
      label: 'Attending',
      value: rsvpStats?.attending ?? 0,
      icon: UserCheck,
      color: 'bg-green-50 text-green-600'
    },
    {
      label: 'Pending',
      value: rsvpStats?.pending ?? (rsvpStats?.total_guests ?? 0) - (rsvpStats?.total_rsvps ?? 0),
      icon: Clock,
      color: 'bg-amber-50 text-amber-600'
    }
  ];

  if (loading) {
    return (
      <CoupleNavigation couple={couple} isPublished={isPublished} onPublishToggle={handlePublishToggle}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-rose-500 animate-spin" />
        </div>
      </CoupleNavigation>
    );
  }

  if (error) {
    return (
      <CoupleNavigation couple={couple} isPublished={isPublished} onPublishToggle={handlePublishToggle}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <button
              onClick={loadData}
              className="px-4 py-2 bg-rose-500 text-white rounded-lg hover:bg-rose-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </CoupleNavigation>
    );
  }

  return (
    <CoupleNavigation couple={couple} isPublished={isPublished} onPublishToggle={handlePublishToggle}>
      <div className="p-4 md:p-8 max-w-5xl mx-auto">
        {/* Welcome header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Welcome{coupleNames ? `, ${coupleNames}` : ''}!
          </h1>
          <p className="text-gray-500 mt-1">Manage your wedding page and guest list</p>
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">How It Works</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                step: '1',
                title: 'Design Your Page',
                desc: 'Add your wedding details, choose a template and colours, upload photos.',
                action: () => navigate('/couple/builder'),
                actionLabel: 'Page Builder',
                color: 'bg-rose-100 text-rose-600'
              },
              {
                step: '2',
                title: 'Add Your Guests',
                desc: 'Add guests one by one or import from an Excel spreadsheet.',
                action: () => navigate('/couple/guests'),
                actionLabel: 'Guest List',
                color: 'bg-blue-100 text-blue-600'
              },
              {
                step: '3',
                title: 'Share the Link',
                desc: 'Copy your invite message and paste it into your WhatsApp groups.',
                action: null,
                actionLabel: null,
                color: 'bg-green-100 text-green-600'
              },
              {
                step: '4',
                title: 'Track RSVPs',
                desc: 'Guests find their name on your page and RSVP. You see responses here.',
                action: () => navigate('/couple/rsvps'),
                actionLabel: 'View RSVPs',
                color: 'bg-purple-100 text-purple-600'
              }
            ].map(({ step, title, desc, action, actionLabel, color }) => (
              <div key={step} className="relative">
                <div className={`w-8 h-8 rounded-full ${color} flex items-center justify-center text-sm font-bold mb-3`}>
                  {step}
                </div>
                <h3 className="font-semibold text-gray-800 text-sm mb-1">{title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed mb-2">{desc}</p>
                {action && (
                  <button
                    onClick={action}
                    className="text-xs text-rose-600 hover:text-rose-700 font-medium"
                  >
                    {actionLabel} &rarr;
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Countdown + Published status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {/* Countdown card */}
          <div className="bg-gradient-to-br from-rose-500 to-pink-500 rounded-2xl p-6 text-white">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5" />
              <span className="text-sm font-medium opacity-90">Wedding Countdown</span>
            </div>
            {daysLeft !== null ? (
              <>
                <p className="text-4xl font-bold">{daysLeft}</p>
                <p className="text-sm opacity-80 mt-1">days to go</p>
              </>
            ) : (
              <button
                onClick={() => navigate('/couple/builder')}
                className="mt-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors"
              >
                Set your wedding date
              </button>
            )}
          </div>

          {/* Page URL card */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-500">Your Wedding Page</span>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  isPublished
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {isPublished ? 'Published' : 'Unpublished'}
              </span>
            </div>
            {slug ? (
              <div className="flex items-center gap-2 mt-3">
                <div className="flex-1 bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-700 truncate font-mono">
                  bridalconnect.co.za/{slug}
                </div>
                <button
                  onClick={handleCopyUrl}
                  className="p-2 text-gray-500 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors"
                  title="Copy URL"
                >
                  {copied ? (
                    <Check className="w-5 h-5 text-green-500" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-400 mt-3">No page created yet</p>
            )}
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.label}
                className="bg-white rounded-xl border border-gray-200 p-4"
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${stat.color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-500">{stat.label}</p>
              </div>
            );
          })}
        </div>

        {/* Share Invite Section */}
        {slug && isPublished && (
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8">
            <div className="flex items-center gap-2 mb-1">
              <MessageCircle className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Share Your Invite</h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">Copy this message and paste it into your WhatsApp groups, broadcast lists, or send individually.</p>

            {/* Preview of the invite message */}
            <div className="bg-gray-50 rounded-xl p-4 mb-4 border border-gray-200 font-sans text-sm text-gray-700 whitespace-pre-line leading-relaxed">
              {(() => {
                const weddingDate = couple?.wedding_date || page?.page?.wedding_date;
                const venue = page?.page?.venue_name;
                const dateStr = weddingDate
                  ? new Date(weddingDate).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                  : null;
                const lines = [`*You're invited to ${coupleNames}'s wedding!* 🎉`];
                if (dateStr) lines.push(`\n📅 ${dateStr}`);
                if (venue) lines.push(`📍 ${venue}`);
                lines.push(`\nView all the details and RSVP here:\nhttps://bridalconnect.co.za/${slug}`);
                lines.push(`\nFind your name on the page to RSVP ❤️`);
                return lines.join('\n');
              })()}
            </div>

            <div className="flex flex-wrap gap-3">
              {/* Copy message */}
              <button
                onClick={() => {
                  const weddingDate = couple?.wedding_date || page?.page?.wedding_date;
                  const venue = page?.page?.venue_name;
                  const dateStr = weddingDate
                    ? new Date(weddingDate).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                    : null;
                  const lines = [`*You're invited to ${coupleNames}'s wedding!* 🎉`];
                  if (dateStr) lines.push(`\n📅 ${dateStr}`);
                  if (venue) lines.push(`📍 ${venue}`);
                  lines.push(`\nView all the details and RSVP here:\nhttps://bridalconnect.co.za/${slug}`);
                  lines.push(`\nFind your name on the page to RSVP ❤️`);
                  const msg = lines.join('\n');
                  navigator.clipboard.writeText(msg);
                  setCopiedInvite(true);
                  setTimeout(() => setCopiedInvite(false), 3000);
                }}
                className="flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-colors"
              >
                {copiedInvite ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copiedInvite ? 'Copied!' : 'Copy Message'}
              </button>

              {/* Share via WhatsApp */}
              <button
                onClick={() => {
                  const weddingDate = couple?.wedding_date || page?.page?.wedding_date;
                  const venue = page?.page?.venue_name;
                  const dateStr = weddingDate
                    ? new Date(weddingDate).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                    : null;
                  const lines = [`*You're invited to ${coupleNames}'s wedding!* 🎉`];
                  if (dateStr) lines.push(`\n📅 ${dateStr}`);
                  if (venue) lines.push(`📍 ${venue}`);
                  lines.push(`\nView all the details and RSVP here:\nhttps://bridalconnect.co.za/${slug}`);
                  lines.push(`\nFind your name on the page to RSVP ❤️`);
                  const msg = lines.join('\n');
                  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                }}
                className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                Share via WhatsApp
              </button>

              {/* Native share (mobile) */}
              {typeof navigator.share === 'function' && (
                <button
                  onClick={() => {
                    navigator.share({
                      title: `${coupleNames}'s Wedding`,
                      text: `You're invited to ${coupleNames}'s wedding! View details and RSVP:`,
                      url: `https://bridalconnect.co.za/${slug}`
                    }).catch(() => {});
                  }}
                  className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  <Share2 className="w-4 h-4" />
                  More Options
                </button>
              )}
            </div>

            <p className="text-xs text-gray-400 mt-3">
              Tip: Paste this into your family WhatsApp groups, friend groups, or use WhatsApp's Broadcast List to send to multiple contacts at once.
            </p>
          </div>
        )}

        {/* Publish prompt if not published */}
        {slug && !isPublished && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h2 className="text-lg font-semibold text-amber-800 mb-2">Your page isn't published yet</h2>
            <p className="text-sm text-amber-700 mb-4">Publish your wedding page so you can share it with your guests.</p>
            <button
              onClick={handlePublishToggle}
              className="px-5 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-medium hover:bg-amber-700 transition-colors"
            >
              Publish Now
            </button>
          </div>
        )}

        {/* Quick actions */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button
              onClick={() => navigate('/couple/builder')}
              className="flex items-center gap-3 px-4 py-3 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded-xl transition-colors text-left"
            >
              <Palette className="w-5 h-5" />
              <span className="font-medium text-sm">Edit Page</span>
            </button>
            <button
              onClick={() => navigate('/couple/guests')}
              className="flex items-center gap-3 px-4 py-3 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-xl transition-colors text-left"
            >
              <Users className="w-5 h-5" />
              <span className="font-medium text-sm">Manage Guests</span>
            </button>
            <button
              onClick={() => navigate('/couple/rsvps')}
              className="flex items-center gap-3 px-4 py-3 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-xl transition-colors text-left"
            >
              <ClipboardCheck className="w-5 h-5" />
              <span className="font-medium text-sm">View RSVPs</span>
            </button>
          </div>
        </div>
      </div>
    </CoupleNavigation>
  );
}

export default CoupleDashboard;
